/* eslint-disable react/jsx-indent */
/*
 * Plan route payment summary visual backed by the koodo-ui-kit
 * <PlanCardFooter /> component.
 */
import React, {
  useContext, useEffect, useMemo, useState
} from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { PlanCardFooter as KoodoPlanCardFooter } from '@telus/koodo-ui-kit'

import useIsMobileScreen from '@apps/hooks/isMobileScreen'

import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import { PHONE_NUMBER_STATE } from '@apps/redux/features/PhoneNumberSlice'
import { PAYMENT_STATE } from '@apps/redux/features/PayAndReviewSlice'
import { ACCOUNT_SETUP_STATE } from '@apps/redux/features/AccountSetupSlice'
import ProxyOperations from '@apps/flows/activation/services/operations'
import { fetchServiceFee911 } from '@apps/utils/serviceFee911'
import { PAYMENT_OPTIONS, SIM_OPTIONS } from '@apps/utils/constants'
import { getById, getBySlug } from '@apps/utils/contentful'
import { toPlainFeatureText } from '@apps/utils/featureText'
import Sites from '@sites/index'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'

const getServiceFeeAmount = (serviceFeesResponse) => {
  const fee = serviceFeesResponse?.[0]?.fee
  return typeof fee === 'number' && Number.isFinite(fee) ? fee / 100 : null
}

const PlanCardFooter = () => {
  const history = useHistory()
  const location = useLocation()
  const isMobileScreen = useIsMobileScreen()
  const { get911ServiceFees } = ProxyOperations('public')
  const {
    userType,
    content: { pageContent, featureFlags: pageFeatureFlags }
  } = useSelector((state) => state.common)

  const plansPageState = useSelector((state) => state.plansPage)
  const {
    [PLANS_STATE.PLAN]: selectedPlan,
    [PLANS_STATE.PROMO]: planPromo
  } = plansPageState

  const paymentState = useSelector((state) => state.payAndReviewPage)
  const {
    [PAYMENT_STATE.SELECTED_PAYMENT_OPTION]: selectedPaymentOption,
    [PAYMENT_STATE.PROMO.PROMOTION_CODE_INFO]: promotionCodeInfo,
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: multiPromotionCodeInfo,
    [PAYMENT_STATE.VOUCHER.ADDED_VOUCHERS]: addedVouchers
  } = paymentState

  const enableMultiPromo = pageFeatureFlags?.[0]?.enableMultiPromo
  const isServiceFee911Enabled = pageFeatureFlags?.[0]?.isServiceFee911Enabled

  const {
    simType: simSelection,
    cost: simSelectionCost
  } = useSelector((state) => state.simSelectionPage).selectSimType
  const { [ACCOUNT_SETUP_STATE.BAN]: accountBan } = useSelector((state) => state.accountSetupPage)

  const phoneNumberState = useSelector((state) => state.phoneNumberPage) || {}
  const selectedPhoneNumber = phoneNumberState[PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER]
  const createdPhoneNumber =
    phoneNumberState[PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PHONE_NUMBER]
  const phoneNumberForServiceFee = selectedPhoneNumber
    || createdPhoneNumber?.value
    || (typeof createdPhoneNumber === 'string' ? createdPhoneNumber : '')

  const { [APPLICATION_STATE.CURRENT_LANGUAGE]: language } = useContext(ApplicationContext)

  const isDealer = userType === Sites.dealer
  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'

  const text = useMemo(() => {
    if (!pageContent || !language) return {}
    const paymentPage = (getById(pageContent, `paymentPage${pagePrefix}`) || {})[language] || {}
    const plansPage = (getById(pageContent, `plansPage${pagePrefix}`) || {})[language] || {}
    const commonElements = (getById(pageContent, 'commonElements') || {})[language] || {}
    return {
      planTitle: getBySlug(plansPage.shortTextFields, 'yourCurrentOrder')?.value,
      changePlan: getBySlug(plansPage.shortTextFields, 'changePlan')?.value,
      dueTodayTitle: getBySlug(paymentPage.shortTextFields, 'dueNow')?.value,
      dueMonthly: getBySlug(
        commonElements.shortTextFields,
        'dueMonthly'
      )?.value,
      beforeTax: getBySlug(commonElements.shortTextFields, 'beforeTax')?.value,
      serviceFee911Attempt:
        getBySlug(commonElements.shortTextFields, 'get-serviceFee911-attempt')?.value
    }
  }, [pageContent, language, pagePrefix])

  // Tag the resolved fee with both plan and phone number. This prevents a
  // slower response for the previously selected plan from being applied to
  // the current plan while the user moves quickly between plan cards.
  const [resolvedServiceFee, setResolvedServiceFee] = useState({ key: '', value: 0 })
  const serviceFeeRequestKey = !accountBan
    && isServiceFee911Enabled
    && selectedPlan?.id
    && phoneNumberForServiceFee
    ? `${selectedPlan.id}:${phoneNumberForServiceFee}`
    : ''
  const currentServiceFee = resolvedServiceFee.key === serviceFeeRequestKey
    ? resolvedServiceFee.value
    : 0


  useEffect(() => {
    // Skip when the fee for this plan and phone number is already resolved. The
    // effect also depends on values that arrive asynchronously (accountBan among
    // them), so without this it re-fetched an unchanged key — two identical
    // requests for one plan selection. get911ServiceFees takes only pricePlan and
    // phoneNumber, so the same key can only return the same fee.
    if (!serviceFeeRequestKey || resolvedServiceFee.key === serviceFeeRequestKey) return undefined

    let cancelled = false
    const configuredAttempts = Number(text.serviceFee911Attempt)
    const maxAttempts = Number.isFinite(configuredAttempts) && configuredAttempts > 0
      ? Math.max(1, Math.floor(configuredAttempts))
      : 1

    const loadServiceFee = async () => {
      let fee = null
      let attempt = 0
      while (fee === null && attempt < maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetchServiceFee911(
          get911ServiceFees,
          selectedPlan.id,
          phoneNumberForServiceFee
        ).catch(() => null)
        fee = getServiceFeeAmount(response)
        attempt += 1
      }

      if (!cancelled) {
        setResolvedServiceFee({
          key: serviceFeeRequestKey,
          value: fee ?? 0
        })
      }
    }

    loadServiceFee()
    return () => {
      cancelled = true
    }
  }, [
    accountBan,
    isServiceFee911Enabled,
    selectedPlan?.id,
    phoneNumberForServiceFee,
    serviceFeeRequestKey,
    text.serviceFee911Attempt
  ])

  const planSelected = !!selectedPlan?.id
  const eSimSelected = simSelection === SIM_OPTIONS.E_SIM

  if (accountBan) return null
  if (!planSelected && !eSimSelected) return null

  const isVoucherDueNow =
    selectedPaymentOption !== PAYMENT_OPTIONS.CREDIT_CARD &&
    Array.isArray(addedVouchers) &&
    addedVouchers.length > 0

  // Promo priority mirrors OrderSummary's getPromoBundle: manual promo, then
  // multi-promo (behind its flag), then the plan's promo. No stacking.
  // PLANS_STATE.PROMO can be unset when the plan was pre-selected via URL, so
  // the promo embedded on the plan object is the last fallback.
  const getPromoBundle = () => {
    if (promotionCodeInfo) return promotionCodeInfo
    if (enableMultiPromo && multiPromotionCodeInfo?.length > 0) return multiPromotionCodeInfo
    return planPromo || selectedPlan?.promo || null
  }

  // "Due now" (before tax) is computed from source data — plan value + promo
  // fees + SIM cost + the resolved 911 fee — rather than the redux
  // amountOwing/amountDue. The
  // plans/plan routes don't mount <OrderSummary />, so those totals are never
  // recomputed there and hold the stale SIM-only subtotal picked up on the SIM
  // step (e.g. $5), which surfaced as the incorrect "$5 due now" after a plan
  // was selected. Tax is intentionally excluded because the footer is labelled
  // "Before tax".
  const computeBeforeTaxSubtotal = () => {
    const planValue = Number(selectedPlan?.value) || 0
    const simCost = Number(simSelectionCost) || 0
    let subtotal = planValue + simCost + currentServiceFee
    const addBundleFees = (bundles) => {
      if (!Array.isArray(bundles)) return
      bundles.forEach((bundle) => {
        const fee = Number(bundle?.fee)
        if (fee && !Number.isNaN(fee) && Math.abs(fee) !== 0) subtotal += fee
      })
    }
    const promoBundle = getPromoBundle()
    if (promoBundle?.bundles) {
      addBundleFees(promoBundle.bundles)
    } else if (enableMultiPromo && Array.isArray(promoBundle)) {
      promoBundle.forEach((code) => addBundleFees(code?.bundles))
    }
    return subtotal > 0 ? subtotal : 0
  }
  const beforeTaxSubtotal = computeBeforeTaxSubtotal()

  // Voucher path: deduct the added voucher totals locally (same rule as
  // OrderSummary's amountDue) instead of reading the redux
  // amountDue, which is just as stale as amountOwing on these routes.
  const voucherTotal = (addedVouchers || []).reduce(
    (total, voucher) => total + (Number(voucher?.totalAmount) || 0),
    0
  )
  const dueNowAmount = (isVoucherDueNow
    ? Math.max(beforeTaxSubtotal - voucherTotal, 0)
    : beforeTaxSubtotal
  ).toFixed(2)
  // Update immediately from the currently selected plan, then add only the
  // 911 fee resolved for that same plan + phone-number pair. Never reuse the
  // previous plan's non-zero estimatedRecurringCharge.
  const monthlyAmount = (
    (Number(selectedPlan?.value) || 0) + currentServiceFee
  ).toFixed(2)
  const consumerActivationPaths = new ConsumerActivationPaths()
  const changePlanPath = consumerActivationPaths.Plans
  const normalizePath = (path = '') => path.replace(/\/+$/, '') || '/'
  const isOnPlansPage = normalizePath(location.pathname) === normalizePath(changePlanPath)
  const enableChangePlanLink =
    !isDealer &&
    !isOnPlansPage &&
    !!text.changePlan &&
    !!selectedPlan?.sluggedName

  const handleFooterClick = (event) => {
    const anchor = event.target.closest?.('a')
    if (!enableChangePlanLink || !anchor || anchor.getAttribute('href') !== changePlanPath) return
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) return

    event.preventDefault()
    history.push(changePlanPath, { from: location.pathname })
  }

  // The kit renders planDetailsFooter as a plain string, so the emphasis
  // markup plan features carry ({{text}} wrappers, ** markdown, footnote
  // superscripts) has to be flattened or it shows up verbatim.
  const features = toPlainFeatureText(
    selectedPlan?.features?.[0]
    || selectedPlan?.planData
    || selectedPlan?.infoText
    || selectedPlan?.name
    || ''
  )

  const planPromoText = (selectedPlan?.promo?.bundles || [])
    .map((bundle) => toPlainFeatureText(bundle?.name))
    .filter(Boolean)
    .join(', ')
  const detailsFooterText = planPromoText
    ? [features, planPromoText].filter(Boolean).join(', ')
    : features

  return (
    <div
      data-component="payment-summary"
      className="kpre-payment-summary-shell kpre-plan-card-footer sticky bottom-0 z-50 w-full"
      onClickCapture={handleFooterClick}
    >
      <div data-component="payment-summary-cookie-anchor" />
      <KoodoPlanCardFooter
        enablePlanDetailsHeading={!isMobileScreen}
        planDetailsHeading={text.planTitle || ''}
        planDetailsFooter={detailsFooterText}
        priceLockupTopTextOne={text.dueTodayTitle || ''}
        priceLockupTopPriceOne={dueNowAmount}
        priceLockupBottomTextOne={text.beforeTax || ''}
        priceLockupTopTextTwo={text.dueMonthly || ''}
        priceLockupTopPriceTwo={monthlyAmount}
        priceLockupBottomTextTwo={text.beforeTax || ''}
        priceCurrency="$"
        language={language}
        enableFooterLink={enableChangePlanLink}
        footerLinkText={text.changePlan}
        footerLinkHref={changePlanPath}
      />
    </div>
  )
}

export default PlanCardFooter
